import { Outlet, useLocation, Link } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { useAtomValue, useAtom } from 'jotai'
import { isPortalAtom, selectedSensorCardAtom, filterOpenAtom, sortOpenAtom } from '@/core/store/globalStore'
import Header from '@/components/header/Headers'
import { useState, useEffect, useRef } from 'react'
import { Icon, Tooltip, Button } from '@equinor/eds-core-react'
import {
    dashboard,
    home,
    menu as menuIcon,
    close as closeIcon,
    collapse,
    expand,
    library_books as portfolio,
    info_circle as info,
    help_outline as help,
    account_circle as user,
    thumbs_up_down,
    donut_large as plantStatus,
} from '@equinor/eds-icons'
import { baseFont, EdsStyledIcon } from '@/styles'
import { SocketErrorDisplayBanner } from '@/features/dashboard/components/Banner/SocketErrorDisplayBanner'
import { ErrorDisplayBanner } from '@/features/dashboard/components/Banner/ErrorDisplayBanner'
import { useCurrentPlant } from '@/core/hooks-and-services/useCurrentPlant'

const sidebarLinksMobile = [
    { label: 'IIoT', icon: home, path: '/' },
    { label: 'Sensor overview', icon: dashboard, path: '/dashboard' },
    {
        label: 'Menu',
        icon: menuIcon,
        path: '#',
        children: [
            { label: 'Portfolio', icon: portfolio, path: '/portfolio' },
            { label: 'About IIoT', icon: info, path: '/about' },
            { label: 'How to get started', icon: help, path: '/howto' },
            {
                label: 'Feedback',
                icon: thumbs_up_down,
                path: 'https://forms.office.com/Pages/ResponsePage.aspx?id=NaKkOuK21UiRlX_PBbRZsD3jOHqSMdpJknKjfcoA3wZUOUZDMUpWWDBSSEE1QUkwNUtHUEVBWTNZMyQlQCN0PWcu',
            },
            { label: 'Profile & settings', icon: user, path: '/user' },
        ],
    },
]

const sidebarLinksDesktop = [
    { label: 'IIoT', icon: home, path: '/' },
    { label: 'Sensor overview', icon: dashboard, path: '/dashboard' },
    { label: 'Plant status', icon: plantStatus, path: '/status' },
    { label: 'Portfolio', icon: portfolio, path: '/portfolio' },
    { label: 'About IIoT', icon: info, path: '/about' },
    { label: 'How to get started', icon: help, path: '/howto' },
]

const Layout = () => {
    const portal = useAtomValue(isPortalAtom)
    const [selectedSensorCard, setSelectedSensorCard] = useAtom(selectedSensorCardAtom)
    const [filterOpen, setFilterOpen] = useAtom(filterOpenAtom)
    const [sortOpen, setSortOpen] = useAtom(sortOpenAtom)
    const location = useLocation()
    const [menuOpen, setMenuOpen] = useState(false)
    const [collapsed, setCollapsed] = useState(false)
    const [windowWidth, setWindowWidth] = useState(window.innerWidth)
    const { currentPlant } = useCurrentPlant()
    const contentRef = useRef<HTMLElement>(null)
    const section = location.pathname.split('/')[1] ?? ''

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth)
        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    useEffect(() => {
        setMenuOpen(false)
    }, [location.pathname])

    // <Content> is the scroll container and stays mounted across navigations, so reset it manually.
    // Keyed on the top-level segment only, so in-page url updates (e.g. expanding a portfolio card) keep their position.
    useEffect(() => {
        contentRef.current?.scrollTo({ top: 0, left: 0 })
        window.scrollTo({ top: 0, left: 0 })
    }, [section])

    const toggleCollapse = () => setCollapsed((prev) => !prev)

    return (
        <Wrapper>
            {!portal && (
                <>
                    <HeaderContainer>
                        <Header />
                    </HeaderContainer>

                    <MainContainer>
                        <SidebarWrapper>
                            <SidebarContainer $collapsed={collapsed}>
                                <CollapseToggle onClick={toggleCollapse}>
                                    <Icon data={collapsed ? expand : collapse} size={18} />
                                </CollapseToggle>

                                {windowWidth <= 1024 &&
                                    sidebarLinksMobile.map((link) => (
                                        <div key={link.label} title={link.label}>
                                            {windowWidth <= 1024 && link.label == 'Menu' ? (
                                                <EdsStyledLink
                                                    key={link.label}
                                                    as={Link}
                                                    $active={location.pathname === link.path || menuOpen}
                                                    onClick={() => setMenuOpen(!menuOpen)}
                                                    to={link.path}
                                                >
                                                    <EdsStyledIcon data={link.icon} size={24} />
                                                    <Label $collapsed={collapsed}>{link.label}</Label>
                                                </EdsStyledLink>
                                            ) : (
                                                <EdsStyledLink
                                                    key={link.label}
                                                    as={Link}
                                                    variant="ghost_icon"
                                                    to={link.path}
                                                    $active={location.pathname === link.path}
                                                    onClick={() => {
                                                        setMenuOpen(false)
                                                        setSelectedSensorCard(null)
                                                    }}
                                                >
                                                    <EdsStyledIcon data={link.icon} size={24} />
                                                    <Label $collapsed={collapsed}>{link.label}</Label>
                                                </EdsStyledLink>
                                            )}
                                        </div>
                                    ))}

                                {windowWidth > 1024 &&
                                    sidebarLinksDesktop.map((link) => (
                                        <div key={link.label} title={link.label}>
                                            <EdsStyledLink
                                                key={link.label}
                                                as={Link}
                                                variant="ghost_icon"
                                                to={link.path}
                                                $active={location.pathname === link.path}
                                                onClick={() => setSelectedSensorCard(null)}
                                            >
                                                <EdsStyledIcon data={link.icon} size={24} />
                                                <Label $collapsed={collapsed}>{link.label}</Label>
                                            </EdsStyledLink>
                                        </div>
                                    ))}
                            </SidebarContainer>

                            {windowWidth <= 1024 && menuOpen && (
                                <Menu>
                                    <MenuHeader>
                                        <strong>Menu</strong>
                                        <CloseButton onClick={() => setMenuOpen(false)}>
                                            <span>Close</span>
                                            <Icon data={closeIcon} size={24} />
                                        </CloseButton>
                                    </MenuHeader>
                                    <MenuContent>
                                        {sidebarLinksMobile
                                            .find((link) => link.label === 'Menu')
                                            ?.children?.map((child) =>
                                                child.label === 'Feedback' ? (
                                                    <EdsStyledLink
                                                        key={child.label}
                                                        href={child.path}
                                                        as={Link}
                                                        to={child.path}
                                                        $active={location.pathname === child.path}
                                                        target="_blank"
                                                        onClick={() => setMenuOpen(!menuOpen)}
                                                    >
                                                        <EdsStyledIcon data={child.icon} size={24} />
                                                        <Label $collapsed={collapsed}>{child.label}</Label>
                                                    </EdsStyledLink>
                                                ) : (
                                                    <EdsStyledLink
                                                        key={child.label}
                                                        as={Link}
                                                        variant="ghost_icon"
                                                        to={child.path}
                                                        $active={location.pathname === child.path}
                                                        onClick={() => setMenuOpen(!menuOpen)}
                                                    >
                                                        <EdsStyledIcon data={child.icon} size={24} />
                                                        <Label>{child.label}</Label>
                                                        {child.path === '/user' && (
                                                            <PlantHint>
                                                                {currentPlant?.description ?? 'All plants'}
                                                            </PlantHint>
                                                        )}
                                                    </EdsStyledLink>
                                                )
                                            )}
                                    </MenuContent>
                                </Menu>
                            )}
                        </SidebarWrapper>
                        {menuOpen || sortOpen || filterOpen || (windowWidth <= 1024 && selectedSensorCard) ? (
                            <Content ref={contentRef} style={{ overflowY: 'hidden' }}>
                                <BannerContainer>
                                    <BannerBox>
                                        <SocketErrorDisplayBanner />
                                        <ErrorDisplayBanner />
                                    </BannerBox>
                                </BannerContainer>
                                <Outlet />
                            </Content>
                        ) : (
                            <Content ref={contentRef}>
                                <BannerContainer>
                                    <BannerBox>
                                        <SocketErrorDisplayBanner />
                                        <ErrorDisplayBanner />
                                    </BannerBox>
                                </BannerContainer>
                                <Outlet />
                            </Content>
                        )}
                    </MainContainer>
                </>
            )}
        </Wrapper>
    )
}

const baseLinkStyles = css`
    ${baseFont}
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
        background: #eaeaea;
    }

    @media (max-width: 1024px) {
        font-size: 10px;
        padding: 16px;
        gap: 8px;
        flex-direction: row;
    }
`

const EdsStyledLink = styled(Button)<{ $active: boolean }>`
    ${baseLinkStyles}
    background: ${({ $active }) => ($active ? '#E6FAEC' : 'none')};
    color: ${({ $active }) => ($active ? '#007079' : '#1c1c1c')};
    border-radius: 4px;

    &:hover {
        color: #1c1c1c;
        bakcground-color: #f7f7f7;
    }

    @media (max-width: 1024px) {
        background: ${({ $active }) => ($active ? 'white' : '#007079')};
        color: ${({ $active }) => ($active ? '#007079' : 'white')};

        &:hover {
            background: white;
            color: #007079;
        }
    }
`

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100vh;
    min-height: 100dvh;
    min-height: -webkit-fill-available;
    overflow: hidden;
`

const HeaderContainer = styled.div`
    /* The topbar is auto-height (title + plant subtitle); a fixed height let it paint over the content below. */
    min-height: 64px;
    flex: 0 0 auto;
    width: 100%;
    background: white;
    display: flex;
    align-items: center;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    z-index: 10;
    @media (max-width: 1024px) {
        background: #007079;
    }
`

const MainContainer = styled.div`
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;

    @media (max-width: 1024px) {
        flex-direction: column-reverse;
    }
`

const SidebarWrapper = styled.div`
    position: relative;
    display: flex;
    background: #fff;
    height: 84px;

    @media (max-width: 1024px) {
        background: #007079;
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        height: calc(60px + env(safe-area-inset-bottom));
        z-index: 20;
    }
`

const SidebarContainer = styled.nav<{ $collapsed?: boolean }>`
    display: flex;
    flex-direction: column;
    width: ${({ $collapsed }) => ($collapsed ? '60px' : '250px')};
    transition: width 0.3s ease;
    margin-top: 8px;

    @media (max-width: 1024px) {
        flex-direction: row;
        width: 100%;
        height: calc(60px + env(safe-area-inset-bottom));
        padding-bottom: env(safe-area-inset-bottom);
        box-sizing: border-box;
        justify-content: space-around;
        align-items: center;
        border-right: none;
        margin-top: 0;
    }
`

const CollapseToggle = styled.button`
    background: none;
    border: none;
    padding: 12px;
    cursor: pointer;
    display: flex;
    align-items: left;
    justify-content: flex-start;
    color: #666;

    &:hover {
        color: #007079;
        background-color: white;
    }

    @media (max-width: 1024px) {
        display: none;
    }
`

const Label = styled.span<{ $collapsed?: boolean }>`
    ${baseFont}
    display: ${({ $collapsed }) => ($collapsed ? 'none' : 'inline-flex')};
    align-items: center;
    line-height: 1;
    margin-top: 0px;

    @media (max-width: 1024px) {
        display: inline-flex;
        margin-top: 2px;
        white-space: nowrap;
        flex-shrink: 0;
        &:hover {
            background: white;
            color: #007079;
        }
    }
`

const PlantHint = styled.span`
    ${baseFont}
    margin-left: auto;
    padding-left: 16px;
    font-size: 14px;
    opacity: 0.85;
    text-align: right;
    min-width: 0;
`

const Content = styled.main`
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;

    @media (max-width: 1024px) {
        padding-bottom: calc(16px + 60px + env(safe-area-inset-bottom));
    }
`

const Menu = styled.div`
    position: absolute;
    top: 0;
    left: 100%;
    width: 250px;
    height: 434px;
    background: white;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
    z-index: 10;

    @media (max-width: 1024px) {
        position: fixed;
        left: 0;
        right: 0;
        bottom: calc(60px + env(safe-area-inset-bottom));
        top: auto;
        height: 100 %;
        width: 100%;
        background: #007079;
        box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
        z-index: 30;
    }
`

const MenuHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;

    button {
        background: none;
        border: none;
        cursor: pointer;
    }
    @media (max-width: 1024px) {
        background: #007079;
        color: white;
    }
`

const CloseButton = styled.button`
    ${baseFont}
    display: flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    cursor: pointer;
    color: #444;

    &:hover {
        color: #1976d2;
    }
    @media (max-width: 1024px) {
        color: white;
        &:hover {
            color: white;
        }
    }
`

const MenuContent = styled.div`
    display: flex;
    flex-direction: column;
    padding: 16px;
    gap: 12px;
    @media (max-width: 1024px) {
        background: #007079;
    }
`

const BannerBox = styled.div`
    width: 100%;
`

const BannerContainer = styled.div`
    text-align: left;
    position: sticky;
    top: 0;
    left: 0;
    z-index: 1000;
`

export default Layout
